// Friendly error boundary for the Beginner Mode wizard. If anything
// inside <BeginnerShell /> throws during render (hook order regression,
// bad localStorage payload, missing skill data, …) we'd otherwise show
// a fully blank screen because the shell is a fixed-position overlay
// covering the whole viewport. This component swaps that blank screen
// for an actionable recovery card: see the error, retry, exit guided
// mode, or restart from skill #1.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCw, X, RefreshCcw } from "lucide-react";
import {
  getLastBeginnerRender,
  getBeginnerRenderHistory,
} from "./beginnerDebugLog";

interface Props {
  children: ReactNode;
  /** Called when the user picks "Exit guided mode". */
  onExit?: () => void;
  /** Called when the user picks "Restart from skill #1". */
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

class BeginnerShellErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[BeginnerShell] crashed", error, info.componentStack);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[BeginnerShell] last render history before crash:");
      // eslint-disable-next-line no-console
      console.table(getBeginnerRenderHistory());
    }
  }

  private handleRetry = () => this.setState({ error: null });

  private handleExit = () => {
    this.setState({ error: null });
    this.props.onExit?.();
  };

  private handleReset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="fixed inset-0 z-[110] bg-background overflow-y-auto grid place-items-center px-4 py-8"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="beginner-error-title"
      >
        <div className="w-full max-w-md rounded-2xl border border-destructive/40 bg-card p-6 shadow-2xl">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <h2
              id="beginner-error-title"
              className="font-mono font-extrabold text-base text-foreground"
            >
              Guided mode hit a snag
            </h2>
          </div>
          <p className="text-sm font-mono text-muted-foreground leading-relaxed mb-4">
            Something went wrong while rendering this skill. Your progress is
            safe — pick one of the options below to keep going.
          </p>
          <pre className="mb-5 max-h-32 overflow-auto rounded-lg border border-border bg-secondary/40 p-3 text-[10px] font-mono text-muted-foreground whitespace-pre-wrap">
            {error.message || String(error)}
          </pre>
          {import.meta.env.DEV && (() => {
            const last = getLastBeginnerRender();
            if (!last) return null;
            return (
              <details className="mb-5 rounded-lg border border-border bg-secondary/30 p-3">
                <summary className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground cursor-pointer select-none">
                  Last render snapshot (dev only)
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto text-[10px] font-mono text-foreground whitespace-pre-wrap">
                  {JSON.stringify(last, null, 2)}
                </pre>
              </details>
            );
          })()}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <RefreshCcw className="w-4 h-4" />
              Try again
            </button>
            {this.props.onReset && (
              <button
                type="button"
                onClick={this.handleReset}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card/40 px-4 py-2.5 text-xs font-mono text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Restart from skill #1
              </button>
            )}
            {this.props.onExit && (
              <button
                type="button"
                onClick={this.handleExit}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card/40 px-4 py-2.5 text-xs font-mono text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Exit guided mode
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default BeginnerShellErrorBoundary;